import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";

export default function MessageForm({ textId }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setSending(true);
    try {
      const payload = {
        name: name.trim(),
        message: message.trim(),
        text_id: textId || null,
      };
      if (email.trim()) payload.email = email.trim();
      await api.post("/messages", payload);
      setSent(true);
      setName("");
      setEmail("");
      setMessage("");
      toast.success("Gracias por escribir. Tu mensaje ha llegado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="mt-16 pt-12 border-t text-center"
        style={{ borderColor: "var(--rincon-border)" }}
        data-testid="message-sent"
      >
        <div
          className="font-display text-4xl"
          style={{ color: "var(--rincon-secondary)" }}
          aria-hidden="true"
        >
          ❦
        </div>
        <p
          className="mt-4 font-display italic text-2xl"
          style={{ color: "var(--rincon-text)" }}
        >
          Tu mensaje ha llegado.
        </p>
        <p
          className="mt-2 font-body italic"
          style={{ color: "var(--rincon-text-muted)" }}
        >
          Gracias por acompañar estas palabras.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          data-testid="message-send-another"
          className="mt-6 font-display ink-link"
          style={{ color: "var(--rincon-primary)" }}
        >
          Escribir otro mensaje →
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-16 pt-12 border-t"
      style={{ borderColor: "var(--rincon-border)" }}
      data-testid="message-form"
    >
      <div
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] mb-4"
        style={{ color: "var(--rincon-secondary)" }}
      >
        <MessageSquare className="w-3.5 h-3.5" />
        Un mensaje al autor
      </div>
      <h3
        className="font-display text-3xl md:text-4xl tracking-tight"
        style={{ color: "var(--rincon-text)" }}
      >
        ¿Te ha resonado? Escríbeme.
      </h3>
      <p
        className="mt-3 font-body italic"
        style={{ color: "var(--rincon-text-muted)" }}
      >
        Un nombre, un mensaje. Nada más. Tu presencia importa.
      </p>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <label className="block">
          <span
            className="text-xs uppercase tracking-[0.2em]"
            style={{ color: "var(--rincon-text-muted)" }}
          >
            Tu nombre *
          </span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            data-testid="message-name"
            className="mt-2 w-full bg-transparent border-b py-2 font-body text-lg outline-none focus:border-[var(--rincon-primary)] transition-colors"
            style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
          />
        </label>
        <label className="block">
          <span
            className="text-xs uppercase tracking-[0.2em]"
            style={{ color: "var(--rincon-text-muted)" }}
          >
            Correo (opcional)
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="message-email"
            className="mt-2 w-full bg-transparent border-b py-2 font-body text-lg outline-none focus:border-[var(--rincon-primary)] transition-colors"
            style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
          />
        </label>
      </div>

      <label className="block mt-6">
        <span
          className="text-xs uppercase tracking-[0.2em]"
          style={{ color: "var(--rincon-text-muted)" }}
        >
          Tu mensaje *
        </span>
        <textarea
          required
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={4000}
          placeholder="Una palabra, una emoción, un verso tuyo..."
          data-testid="message-body"
          className="mt-2 w-full bg-transparent border p-4 font-body text-base leading-relaxed outline-none resize-y focus:border-[var(--rincon-primary)] transition-colors"
          style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
        />
      </label>

      <div className="mt-6">
        <button
          type="submit"
          disabled={sending}
          data-testid="message-submit"
          className="inline-flex items-center gap-2 px-6 py-3 font-display text-lg transition-colors disabled:opacity-60"
          style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
        >
          <Send className="w-4 h-4" />
          {sending ? "Enviando..." : "Enviar mensaje"}
        </button>
      </div>
    </form>
  );
}
